import { useUser } from "@dashboard/auth/useUser";
import { isAtEntryLocation } from "@dashboard/components/Router";
import { Box, type BoxProps, Text, vars } from "@saleor/macaw-ui-next";
import { type PropsWithChildren } from "react";

import useAppChannel from "../AppChannelContext";
import AppChannelSelect from "../AppChannelSelect";
import { isDetailPopupWindow } from "../consts";
import { ContextualLine } from "../ContextualLinks/ContextualLine";
import { TopNavLink } from "./TopNavLink";
import { TopNavWrapper } from "./TopNavWrapper";

interface TopNavProps {
  title: React.ReactNode;
  subtitle?: React.ReactNode;
  subtitleTop?: React.ReactNode;
  href?: string;
  withoutBorder?: boolean;
  isAlignToRight?: boolean;
  /** Gap between header action buttons. Detail pages use a slightly wider spacing. */
  actionsGap?: 2 | 3;
}

export const Root = ({
  title,
  subtitle,
  subtitleTop,
  href: hrefProp,
  withoutBorder = false,
  isAlignToRight = true,
  actionsGap = 2,
  children,
  ...wrapperProps
}: PropsWithChildren<TopNavProps> & Omit<BoxProps, keyof TopNavProps>) => {
  // 상세 팝업 안에서 옮겨 온 목록(고객의 '주문 보기' 등)은 뒤로 갈 주소가 없어도 뒤로가기를
  // 보인다. 팝업의 뒤로가기는 주소로 가지 않고 직전 페이지로 돌아가므로 주소는 쓰이지 않는다.
  // (2026-10-06 KST 추가)
  const href = hrefProp ?? (isDetailPopupWindow() && !isAtEntryLocation() ? "/" : undefined);

  const { channel, isPickerActive, setChannel } = useAppChannel(false);
  const user = useUser();
  const channels = user?.user?.accessibleChannels ?? [];

  if (subtitleTop && subtitle)
    throw new Error(
      "TopNav is not ready to support both subtitle and subtitleTop. Extend the component or use one of them",
    );

  return (
    <TopNavWrapper
      withoutBorder={withoutBorder}
      hasSubtitleTop={!!subtitleTop}
      hasSubtitle={!!subtitle}
      {...wrapperProps}
    >
      {subtitleTop ? (
        <ContextualLine
          gridColumn="8"
          // The subtitle should be aligned with the title, not back button
          __marginLeft={href ? `calc(${vars.spacing[12]} + ${vars.spacing[1]})` : 0}
          paddingBottom={0}
          __marginBottom="-10px"
        >
          {subtitleTop}
        </ContextualLine>
      ) : null}
      <Box display="flex" alignItems="center" width="100%">
        {href && <TopNavLink to={href} />}
        <Box
          __flex={isAlignToRight ? "1 1 auto" : 0}
          overflow="hidden"
          title={typeof title === "string" ? title : undefined}
        >
          {/*
            페이지 제목은 굵기를 안 줘서 regular(400)로 나왔다. flexg 는 제목을 늘 굵게 쓰고,
            카드 제목(DashboardCard.Title)이 이미 medium(테마에서 700)이라 제목이 그 아래
            단계로 보였다. (2026-09-18 KST 변경)
            카드 제목과 함께 18px → 16px (2026-09-29 KST 수정)
          */}
          <Text size={5} fontWeight="bold" ellipsis display="block">
            {title}
          </Text>
        </Box>
        <Box
          display="flex"
          flexWrap="nowrap"
          height="100%"
          gap={actionsGap}
          __flex={isAlignToRight ? "initial" : 1}
        >
          {isPickerActive && channels.length > 0 && (
            <AppChannelSelect
              channels={channels}
              selectedChannelId={channel?.id}
              onChannelSelect={setChannel}
            />
          )}
          {children}
        </Box>
      </Box>
      {subtitle ? (
        <ContextualLine
          gridColumn="8"
          // The subtitle should be aligned with the title, not back button
          __marginLeft={href ? `calc(${vars.spacing[12]} + ${vars.spacing[1]})` : 0}
          __marginTop={href ? "-0.6rem" : 0}
        >
          {subtitle}
        </ContextualLine>
      ) : null}
    </TopNavWrapper>
  );
};
